import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, ArrowRight, Trophy } from "lucide-react";
import api from "../lib/api";

export default function StoreKpiTile({ locationId, testId = "store-kpi-tile" }) {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const url = locationId
      ? `/store-kpi/locations/${locationId}?days=30`
      : `/store-kpi/summary`;
    api.get(url).then((r) => setData(r.data)).catch(() => setData({ configured: false }));
  }, [locationId]);

  if (!data) return null;

  const isLocation = !!locationId;

  // Not configured yet — show gentle prompt
  if ((isLocation && (!data.dimensions?.length && !data.questions?.length))
      || (!isLocation && !data.configured)) {
    return (
      <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/50 p-5" data-testid={`${testId}-empty`}>
        <div className="flex items-center gap-2 text-amber-700">
          <Store className="h-5 w-5" />
          <h3 className="font-head text-base font-bold">Store KPI Ranking</h3>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          Nothing is set as a Store KPI yet. Tag any dimension or yes/no question as{" "}
          <span className="font-semibold text-amber-700">Store</span> in Rating Form &amp; Recovery to rank your stores
          independently from waiter SSI.
        </p>
        <button onClick={() => navigate("/app/questions")}
          data-testid={`${testId}-setup`}
          className="mt-3 inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-600">
          Set up store KPIs <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    );
  }

  if (isLocation) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-white p-5" data-testid={testId}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-amber-700">
            <Store className="h-5 w-5" />
            <h3 className="font-head text-base font-bold">Store KPI (last 30d)</h3>
          </div>
          <span className="font-head text-2xl font-bold text-amber-700" data-testid={`${testId}-overall`}>
            {data.overall ?? "–"}<span className="ml-1 text-sm text-slate-400">/100</span>
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {data.dimensions.map((d) => (
            <div key={d.key} className="flex items-center justify-between text-sm" data-testid={`${testId}-dim-${d.key}`}>
              <span className="text-slate-700">{d.label}</span>
              <span className="font-semibold text-slate-900">
                {d.avg != null ? `${d.avg}/5` : "–"}
                <span className="ml-1 text-xs text-slate-400">({d.count})</span>
              </span>
            </div>
          ))}
          {data.questions.map((q) => (
            <div key={q.text} className="flex items-start justify-between gap-3 text-sm" data-testid={`${testId}-q`}>
              <span className="flex-1 text-slate-700">{q.text}</span>
              <span className="font-semibold text-slate-900">
                {q.yes_pct != null ? `${q.yes_pct}%` : "–"}
                <span className="ml-1 text-xs text-slate-400">({q.count})</span>
              </span>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-slate-400">Store KPIs are independent of individual waiter SSI.</p>
      </div>
    );
  }

  // org summary
  return (
    <div className="rounded-2xl border border-amber-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-700">
          <Store className="h-5 w-5" />
          <h3 className="font-head text-base font-bold">Store KPI Ranking</h3>
        </div>
        <button onClick={() => navigate("/app/store-kpi")} data-testid={`${testId}-open`}
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800">
          Open ranking <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Org average</p>
          <p className="font-head text-2xl font-bold text-amber-700" data-testid={`${testId}-avg`}>
            {data.org_average ?? "–"}<span className="ml-1 text-sm text-slate-400">/100</span>
          </p>
          <p className="text-[11px] text-slate-400">{data.locations_rated} store{data.locations_rated === 1 ? "" : "s"} ranked</p>
        </div>
        <div className="rounded-xl bg-amber-50 p-3">
          <p className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-amber-700">
            <Trophy className="h-3 w-3" /> Top store
          </p>
          {data.top ? (
            <>
              <p className="truncate font-head text-sm font-bold text-slate-900" data-testid={`${testId}-top-name`}>
                {data.top.location_name}
              </p>
              <p className="text-sm font-semibold text-amber-700">{data.top.overall}/100</p>
            </>
          ) : (
            <p className="text-xs text-slate-400">No ratings yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
